import { api } from './api';
import type { 
  AffiliateApplication, 
  AffiliateProfile, 
  ReferralTracking, 
  CommissionRecord, 
  CommissionPayment, 
  AffiliateActivityLog,
  AffiliateApplicationFormData,
  AffiliateProfileUpdateData,
  AffiliateDashboardData,
  AdminAffiliateDashboardData
} from '../types';

interface PaginatedResponse<T> {
  count: number;
  results: T[];
  next?: string | null;
  previous?: string | null;
}

export const AffiliatesAPI = {
  // Affiliate Applications
  async submitApplication(formData: AffiliateApplicationFormData, w9File?: File): Promise<AffiliateApplication> {
    const data = new FormData();
    
    // Add form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value.toString());
      }
    });
    
    // Add W9 document if provided
    if (w9File) {
      data.append('w9_document', w9File);
    }
    
    // For FormData, we need to use fetch directly to avoid content-type header issues
    // No Authorization header needed since this is a public signup endpoint
    const response = await fetch('/api/affiliates/applications/', {
      method: 'POST',
      body: data,
    });
    
    if (!response.ok) {
      throw new Error('Failed to submit application');
    }
    
    return await response.json() as AffiliateApplication;
  },

  async getApplications(): Promise<AffiliateApplication[]> {
    const res = await api.get<AffiliateApplication[] | PaginatedResponse<AffiliateApplication>>('/api/affiliates/applications/');
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async getApplication(id: string): Promise<AffiliateApplication> {
    const res = await api.get<AffiliateApplication>(`/api/affiliates/applications/${id}/`);
    return res.data;
  },

  async approveApplication(id: string): Promise<{ affiliate_profile: AffiliateProfile; message: string }> {
    const res = await api.post<{ affiliate_profile: AffiliateProfile; message: string }>(
      `/api/affiliates/applications/${id}/approve/`, {}
    );
    return res.data;
  },

  async rejectApplication(id: string, reason: string): Promise<{ message: string }> {
    const res = await api.post<{ message: string }>(
      `/api/affiliates/applications/${id}/reject/`, 
      { rejection_reason: reason }
    );
    return res.data;
  },

  // Affiliate Profiles
  async getProfiles(): Promise<AffiliateProfile[]> {
    const res = await api.get<AffiliateProfile[] | PaginatedResponse<AffiliateProfile>>('/api/affiliates/profiles/');
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async getProfile(id?: string): Promise<AffiliateProfile> {
    const url = id ? `/api/affiliates/profiles/${id}/` : '/api/affiliates/profiles/me/';
    const res = await api.get<AffiliateProfile>(url);
    return res.data;
  },

  async updateProfile(id: string, data: AffiliateProfileUpdateData): Promise<AffiliateProfile> {
    const res = await api.patch<AffiliateProfile>(`/api/affiliates/profiles/${id}/`, data);
    return res.data;
  },

  async updateMyProfile(data: AffiliateProfileUpdateData): Promise<AffiliateProfile> {
    const res = await api.patch<AffiliateProfile>('/api/affiliates/profiles/me/', data);
    return res.data;
  },

  async generateNewReferralCode(id?: string): Promise<{ referral_code: string }> {
    const url = id ? `/api/affiliates/profiles/${id}/generate-referral-code/` : '/api/affiliates/profiles/me/generate-referral-code/';
    const res = await api.post<{ referral_code: string }>(url, {});
    return res.data;
  },

  // Referral Tracking
  async getReferralTrackings(affiliateId?: string): Promise<ReferralTracking[]> {
    const query = affiliateId ? `?affiliate=${affiliateId}` : '';
    const res = await api.get<ReferralTracking[] | PaginatedResponse<ReferralTracking>>(`/api/affiliates/referral-trackings/${query}`);
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async createReferralTracking(data: {
    referral_code: string;
    organization_id: string;
    source_url?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    ip_address?: string;
    user_agent?: string;
  }): Promise<ReferralTracking> {
    const res = await api.post<ReferralTracking>('/api/affiliates/referral-trackings/', data);
    return res.data;
  },

  // Commission Records
  async getCommissionRecords(affiliateId?: string): Promise<CommissionRecord[]> {
    const query = affiliateId ? `?affiliate=${affiliateId}` : '';
    const res = await api.get<CommissionRecord[] | PaginatedResponse<CommissionRecord>>(`/api/affiliates/commission-records/${query}`);
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async getCommissionRecord(id: string): Promise<CommissionRecord> {
    const res = await api.get<CommissionRecord>(`/api/affiliates/commission-records/${id}/`);
    return res.data;
  },

  async calculateCommissions(): Promise<{ message: string; records_processed: number }> {
    const res = await api.post<{ message: string; records_processed: number }>('/api/affiliates/commission-records/calculate/', {});
    return res.data;
  },

  async approveCommission(id: string): Promise<CommissionRecord> {
    const res = await api.post<CommissionRecord>(`/api/affiliates/commission-records/${id}/approve/`, {});
    return res.data;
  },

  // Commission Payments
  async getCommissionPayments(affiliateId?: string): Promise<CommissionPayment[]> {
    const query = affiliateId ? `?affiliate=${affiliateId}` : '';
    const res = await api.get<CommissionPayment[] | PaginatedResponse<CommissionPayment>>(`/api/affiliates/commission-payments/${query}`);
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  async createCommissionPayment(data: {
    affiliate: string;
    payment_method: 'ach' | 'check' | 'wire';
    period_start: string;
    period_end: string;
    total_amount: number;
  }): Promise<CommissionPayment> {
    const res = await api.post<CommissionPayment>('/api/affiliates/commission-payments/', data);
    return res.data;
  },

  async processPayment(id: string): Promise<CommissionPayment> {
    const res = await api.post<CommissionPayment>(`/api/affiliates/commission-payments/${id}/process/`, {});
    return res.data;
  },

  // Activity Logs
  async getActivityLogs(affiliateId?: string): Promise<AffiliateActivityLog[]> {
    const query = affiliateId ? `?affiliate=${affiliateId}` : '';
    const res = await api.get<AffiliateActivityLog[] | PaginatedResponse<AffiliateActivityLog>>(`/api/affiliates/activity-logs/${query}`);
    if (Array.isArray(res.data)) {
      return res.data;
    } else if (res.data && 'results' in res.data) {
      return res.data.results;
    }
    return [];
  },

  // Dashboard APIs
  async getAffiliateDashboard(): Promise<AffiliateDashboardData> {
    const res = await api.get<AffiliateDashboardData>('/api/affiliates/dashboard/');
    return res.data;
  },

  async getAdminAffiliateDashboard(): Promise<AdminAffiliateDashboardData> {
    const res = await api.get<AdminAffiliateDashboardData>('/api/affiliates/admin-dashboard/');
    return res.data;
  },

  // Utility APIs
  async validateReferralCode(code: string): Promise<{ valid: boolean; affiliate?: AffiliateProfile }> {
    const res = await api.get<{ valid: boolean; affiliate?: AffiliateProfile }>(`/api/affiliates/validate-referral-code/${code}/`);
    return res.data;
  },

  async getPerformanceMetrics(affiliateId?: string, period?: 'month' | 'quarter' | 'year'): Promise<{
    total_referrals: number;
    successful_referrals: number;
    conversion_rate: number;
    total_commissions_earned: number;
    total_commissions_paid: number;
    pending_commissions: number;
    average_commission_per_referral: number;
    period_start: string;
    period_end: string;
  }> {
    const queryParams = [];
    if (affiliateId) queryParams.push(`affiliate=${affiliateId}`);
    if (period) queryParams.push(`period=${period}`);
    const query = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    
    const res = await api.get<{
      total_referrals: number;
      successful_referrals: number;
      conversion_rate: number;
      total_commissions_earned: number;
      total_commissions_paid: number;
      pending_commissions: number;
      average_commission_per_referral: number;
      period_start: string;
      period_end: string;
    }>(`/api/affiliates/performance-metrics/${query}`);
    return res.data;
  },

  // Admin-only APIs
  async updateAffiliateStatus(id: string, status: 'active' | 'suspended' | 'terminated', reason?: string): Promise<AffiliateProfile> {
    const res = await api.post<AffiliateProfile>(`/api/affiliates/profiles/${id}/update-status/`, {
      status,
      reason
    });
    return res.data;
  },

  async adjustCommission(id: string, adjustment: number, reason: string): Promise<CommissionRecord> {
    const res = await api.post<CommissionRecord>(`/api/affiliates/commission-records/${id}/adjust/`, {
      adjustment,
      reason
    });
    return res.data;
  },

  async bulkProcessPayments(affiliateIds: string[], paymentMethod: 'ach' | 'check' | 'wire'): Promise<{
    message: string;
    payments_created: number;
    total_amount: number;
  }> {
    const res = await api.post<{
      message: string;
      payments_created: number;
      total_amount: number;
    }>('/api/affiliates/bulk-process-payments/', {
      affiliate_ids: affiliateIds,
      payment_method: paymentMethod
    });
    return res.data;
  },
};